import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  createPortal,
} from 'react-dom';
import {
  Link,
} from 'react-router-dom';
import clsx from 'clsx';
import ToolTip from 'core/components/Tooltip';
import {
  useConfirmDialog,
  useNotify,
} from 'core/hooks';
import DeleteIcon from 'core/icons/delete';
import InfoIcon from 'core/icons/info';
import LinkIcon from 'core/icons/link';
import {
  ClaimSummaryDTO,
  InsuranceDTO,
} from 'dtos';
import head from 'lodash/head';
import isNil from 'lodash/isNil';
import Button from 'pages/Dashboard/components/Button';
import InsuranceEligibility from 'pages/Dashboard/components/InsuranceEligibility';
import Row from 'pages/Dashboard/components/Row';
import {
  SidePanelComponentProps,
} from 'pages/Dashboard/contexts/sidePanel';
import ClaimActivityLogs from 'pages/Dashboard/pages/Billing/ClaimForm/ClaimActivity/ClaimActivityLogs';
import CreateSecondaryButton from 'pages/Dashboard/pages/Billing/Charges/CreateSecondaryButton';
import ClaimSelect from 'pages/Dashboard/pages/Billing/ClaimForm/ClaimSelect';
import {
  useCreateClaim,
  useDeleteClaim,
  useSetClaimInsurance,
} from 'pages/Dashboard/pages/Billing/hooks';
import {
  Claim,
} from 'pages/Dashboard/pages/Billing/types';
import {
  billingEventChannel,
  claimEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useUserFullName,
} from 'pages/Dashboard/hooks/misc';
import {
  formatDateTime,
} from 'utils/date';
import ClaimStatusSelect from 'pages/Dashboard/pages/Billing/ClaimStatusSelect';
import Skeleton from 'core/components/Skeleton';
import Spinner from 'pages/Dashboard/components/Spinner';
import ClaimTags from 'views/EMR/Billing/ClaimTags';
import EditIcon from 'core/icons/edit';
import ClaimNotesPopover from 'pages/Dashboard/pages/Billing/Charges/columns/ClaimNotesPopover';
import {
  isEmptyString,
  notEmptyString,
} from '@populateui/utils';
import IconButton from '@mui/material/IconButton';
import {
 useGetPatientInsurances,
} from 'views/EMR/components/Insurance/hooks';
import PatientInsuranceSelect from 'pages/Dashboard/components/PatientInsuranceSelect';
import {
  getClaimInsuranceValidity,
} from 'pages/Dashboard/pages/Billing/Charges/utils';

type Props = SidePanelComponentProps & {
  claims?: ClaimSummaryDTO[];
  isLoading?: boolean;
  onClaimChange?: (type: 'create' | 'delete' | 'update') => void;
  isOutOfPocket?: boolean;
  selectedClaimId?: number;
  onSelectedClaimChange?: (claimId?: number) => void;
  // Where the claim actions render
  footerElement?: HTMLElement | null;
};

export default function ClaimInfo({
  entity,
  claims = [],
  isLoading,
  onClaimChange,
  isOutOfPocket,
  selectedClaimId: _selectedClaimId,
  onSelectedClaimChange,
  footerElement,
}: Props) {
  const { encounterId, patient, patientId } = entity;
  const selectedClaimId = _selectedClaimId ?? 0;
  const setSelectedClaimId = (claimId: number) => {
    onSelectedClaimChange?.(claimId === 0 ? undefined : claimId);
  };

  const selectedClaim = useMemo(
    () => claims.find(({ claimId }) => claimId === selectedClaimId) ?? null,
    [claims, selectedClaimId],
  );

  const {
    insuranceDetails,
    transmissions: _transmissions = [],
    acknowledgementStatuses = [],
    createdOn,
    lastSubmittedOn,
    lastSubmittedUserName,
    notes,
    secondaryClaimActionState,
  } = selectedClaim ?? {};

  const {
    data: patientInsuranceList,
    isLoading: isLoadingPatientInsurances,
  } = useGetPatientInsurances(patientId ?? 0);

  const claimInsurance = useMemo(
    () => patientInsuranceList?.find(
      (insurance) => insurance.insuranceId === insuranceDetails?.insuranceId,
    ),
    [patientInsuranceList, insuranceDetails],
  );

  useEffect(() => {
    const unsubscribeFromChargesInfoChange = billingEventChannel.on('onChargesInfoChange', (entity) => {
      if (entity.encounterId === encounterId) {
        onClaimChange?.('update');
      }
    });

    return () => {
      unsubscribeFromChargesInfoChange();
    };
  }, [encounterId, selectedClaimId]);

  const hasClaim = claims.length > 0;
  const transmissions = useMemo(
    () => (_transmissions ?? []).slice().reverse(),
    [_transmissions],
  );

  const {
    isPending: deletingClaim,
    mutateAsync: deleteClaim,
  } = useDeleteClaim();
  const {
    isPending: settingClaimInsurance,
    mutateAsync: setClaimInsurance,
  } = useSetClaimInsurance();

  const notify = useNotify();

  const {
    isInvalid: claimInsuranceIsInvalid,
    invalidTooltip: claimInsuranceInvalidTooltip,
  } = getClaimInsuranceValidity(claimInsurance, isLoadingPatientInsurances);

  const onInsuranceChange = async (option: InsuranceDTO | InsuranceDTO[]) => {
    const insurance = option as InsuranceDTO;
    const insuranceId = insurance.insuranceId ?? 0;

    try {
      await setClaimInsurance({
        claimId: selectedClaimId,
        insuranceId: insuranceId === 0 ? null : insuranceId,
      });
      billingEventChannel.emit('onChargesInfoChange', {
        ...entity,
        encounterId,
        id: selectedClaimId,
      });
      billingEventChannel.emit('onClaimUpsert', encounterId);
    } catch (error) {
      console.error(error);
      notify({
        message: 'Unable to update claim insurance',
        variant: 'error',
      });
    }
  };

  const { confirm, closeConfirm } = useConfirmDialog();
  const onDeleteConfirmed = async () => {
    try {
      await deleteClaim(selectedClaimId);
      notify({
        message: 'Claim deleted successfully',
        variant: 'success',
      });
      const { claimId: firstClaimId } = claims
        .find(({ claimId }) => selectedClaimId !== claimId) ?? {};
      setSelectedClaimId(firstClaimId ?? 0);
      onClaimChange?.('delete');
      billingEventChannel.emit('onChargesInfoChange', {
        encounterId,
        id: firstClaimId ?? 0,
      });
      billingEventChannel.emit('onClaimUpsert', encounterId);
      billingEventChannel.emit('onClaimLogChange', firstClaimId ?? 0);
    } finally {
      closeConfirm();
    }
  };

  const onDelete = () => {
    confirm({
      title: 'Delete Claim',
      onConfirm: onDeleteConfirmed,
      content: (
        <p className="mb-4">
          Are you sure, you want to delete this claim?
        </p>
      ),
    });
  };

  const { mutateAsync: createClaim, isPending: isLoadingCreateClaim } = useCreateClaim();

  const onClaimCreate = async () => {
    claimEventChannel.emit('onClaimCreating', encounterId);
    const claimBundle = await createClaim(encounterId);
    const claimId = claimBundle?.claim?.claimId ?? 0;
    setSelectedClaimId(claimId);
    onClaimChange?.('create');
    billingEventChannel.emit('onChargesInfoChange', {
      encounterId,
      id: claimId,
    });
    billingEventChannel.emit('onClaimUpsert', encounterId);
    claimEventChannel.emit('onClaimCreated', encounterId);
  };

  const notesLabelRef = useRef<HTMLDivElement>(null);
  const [notesPopoverAnchorEl, setNotesPopoverAnchorEl] = useState<HTMLElement | null>(null);

  const updatedBy = useUserFullName(head(transmissions)?.userId);
  const showSubmittedRow = notEmptyString(lastSubmittedOn) || notEmptyString(lastSubmittedUserName);

  return (!hasClaim && !isLoading) ? (
    <div className="p-2">
      {isOutOfPocket ? (
        <div className="flex items-center text-xs font-semibold text-gray-500 gap-1">
          <InfoIcon className="!w-4 !h-4" />
          Claim is not available for Out-of-pocket appointments
        </div>
      ) : (
        <div className="flex flex-col gap-2 w-full text-sm justify-center items-center">
          <Button
            variant="outlined"
            className={clsx('!border-dashed !block !w-full !max-w-[unset]', {
              '!border-blue-500': !isLoadingCreateClaim,
            })}
            color="primary"
            onClick={onClaimCreate}
            disabled={isLoadingCreateClaim}
          >
            {isLoadingCreateClaim ? 'Creating...' : 'Create Claim'}
          </Button>
        </div>
      )}
    </div>
  ) : (
    <>
      <div className="flex flex-col">
        <div className="flex flex-col">
          <Row
            title="Claim ID"
            isLoading={isLoading}
            classes={{
              skeleton: '!w-40',
              root: '!h-12',
            }}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="w-fit max-w-1/2 flex items-center justify-start truncate">
                <ClaimSelect
                  value={selectedClaim}
                  showStatus={false}
                  acknowledgementStatus={head(acknowledgementStatuses ?? [])}
                  options={claims}
                  onChange={(claim: ClaimSummaryDTO | ClaimSummaryDTO[]) => {
                    setSelectedClaimId((claim as ClaimSummaryDTO)?.claimId ?? 0);
                  }}
                />
              </div>
            </div>
          </Row>
          <Row
            title="Claim Status"
            classes={{ skeleton: '!w-32' }}
            isLoading={isLoading}
          >
            <div className="gap-2 flex items-center">
              <ClaimStatusSelect
                claim={{
                  ...selectedClaim,
                  encounterId,
                }}
                classes={{ select: '!py-[3px] !font-medium' }}
              />
            </div>
          </Row>
          <Row
            title="Created"
            isLoading={isLoading}
            classes={{ skeleton: '!w-28' }}
          >
            <strong>{formatDateTime(createdOn ?? '')}</strong>
          </Row>
          {(transmissions.length > 0 || isLoading) && (
            <Row
              title="Updated"
              isLoading={isLoading}
              classes={{ skeleton: '!w-32' }}
            >
              <div className="flex items-center gap-2">
                <strong>{formatDateTime(head(transmissions)?.time ?? '')}</strong>
                {!isNil(updatedBy) && (
                  <span className="text-xs font-bold">
                    {'(By '}
                    {updatedBy}
                    )
                  </span>
                )}
              </div>
            </Row>
          )}
          {showSubmittedRow && (
            <Row
              title="Last Submitted"
              isLoading={isLoading}
              classes={{ skeleton: '!w-40' }}
            >
              <div className="flex items-center gap-2">
                {notEmptyString(lastSubmittedOn) && (
                  <strong>{formatDateTime(lastSubmittedOn ?? '')}</strong>
                )}
                {notEmptyString(lastSubmittedUserName) && (
                  <span className="text-xs font-bold">
                    {`(By ${lastSubmittedUserName})`}
                  </span>
                )}
              </div>
            </Row>
          )}
          <Row
            title="Insurance"
            isLoading={isLoading || isLoadingPatientInsurances}
            classes={{ skeleton: '!w-48' }}
          >
            {settingClaimInsurance ? (
              <div className="flex items-center gap-2">
                <Spinner />
                <span>Updating...</span>
              </div>
            ) : (
              <PatientInsuranceSelect
                key={selectedClaimId}
                patientId={patientId ?? 0}
                insuranceId={insuranceDetails?.insuranceId ?? 0}
                onChange={onInsuranceChange}
                disabled={isLoading || isLoadingPatientInsurances}
                invalid={claimInsuranceIsInvalid}
                invalidTooltip={claimInsuranceInvalidTooltip}
                fitContent
                showInsuranceSlot={false}
                classes={{
                  rootContainer: 'justify-end',
                  select: '!py-[3px] rounded',
                }}
              />
            )}
          </Row>
          {(!isNil(claimInsurance) || isLoading) && (
              <Row
                title="Insurance Eligibility"
                isLoading={isLoading}
                classes={{ skeleton: '!w-32' }}
              >
                <InsuranceEligibility
                  insurance={claimInsurance ?? {}}
                  patient={patient}
                />
              </Row>
          )}
        </div>
        <div className="flex flex-col gap-2 text-xs p-2 border-t border-t-gray-300">
          <div className="font-semibold text-gray-500">
            Tags:
          </div>
          <ClaimTags claimId={selectedClaimId} encounterId={encounterId} />
        </div>
        <div className="flex flex-col gap-2 text-xs p-2 border-y border-y-gray-300">
          <div className="flex items-center gap-2" ref={notesLabelRef}>
            <span className="font-semibold text-gray-500">Notes</span>
            <ToolTip title={isEmptyString(notes) ? 'Add Notes' : 'Edit Notes'}>
              <IconButton
                size="small"
                color="info"
                className="!p-1"
                onClick={(event) => {
                  event.stopPropagation();
                  setNotesPopoverAnchorEl(notesLabelRef?.current);
                }}
              >
                <EditIcon className="!w-5 !h-5 !text-gray-500" />
              </IconButton>
            </ToolTip>
          </div>
          {isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="w-64 !h-3" />
              <Skeleton className="w-80 !h-3" />
              <Skeleton className="w-48 !h-3" />
            </div>
          ) : <span className="whitespace-pre-wrap">{notes ?? '--'}</span>}
        </div>
        <div className="p-2 flex flex-col gap-2 text-xs">
          <span className="font-semibold text-gray-500">Activity</span>
          <div className="p-2">
            <ClaimActivityLogs claimId={selectedClaimId} />
          </div>
        </div>
      </div>
      {!isNil(notesPopoverAnchorEl) && (
        <ClaimNotesPopover
          claimId={selectedClaimId}
          encounterId={encounterId}
          notes={notes ?? ''}
          classes={{ popover: '!w-[590px]' }}
          anchorEl={notesPopoverAnchorEl}
          onClose={() => {
            setNotesPopoverAnchorEl(null);
          }}
        />
      )}
      {!isLoading && !isNil(footerElement) && createPortal(
        (
          <div className="p-4 flex justify-between items-center border-t border-gray-300">
            <div className="flex gap-4">
              {!isNil(selectedClaimId) && (
                <div className="flex items-center gap-2">
                  <Link
                    to={`/billing/claims/${selectedClaimId}`}
                    className="border border-blue-500 rounded text-blue-500 p-2 text-xs font-semibold flex gap-1 items-center !h-8 !py-1.5"
                  >
                    Open Form
                    <LinkIcon className="!text-xs text-inherit" />
                  </Link>
                </div>
              )}
              {secondaryClaimActionState?.isVisible && (
                <CreateSecondaryButton
                  claim={selectedClaim as Claim}
                  patientId={patient?.patientId ?? 0}
                  className="!h-8 !py-1.5 !px-2 !border-blue-500 gap-1"
                >
                  Secondary Claim
                </CreateSecondaryButton>
              )}
            </div>
            {!isNil(selectedClaimId) && (
              <Button
                variant="outlined"
                color="error"
                aria-label="Delete Claim"
                disabled={isLoading || deletingClaim}
                onClick={onDelete}
                className="border !border-red-500 rounded text-xs font-semibold flex gap-1 items-center !h-8 !px-2"
              >
                <DeleteIcon className="!w-4 !h-4 text-red-500 mt-0.5" />
                <span className="text-red-500">Delete</span>
              </Button>
            )}
          </div>
        ), footerElement,
      )}
    </>
  );
}
